import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';

import useChartOptions from '@/components/Chart/hooks/options/useChartOptions';
import useForwardCurveOptions from '@/components/Chart/hooks/options/useForwardCurveOptions';
import useSpotOptions from '@/components/Chart/hooks/options/useSpotOptions';
import useChartSeries from '@/components/Chart/hooks/useChartSeries';
import useForwardCurveChartSeries from '@/components/Chart/hooks/useForwardCurveChartSeries';
import RegularChart from '@/components/Chart/RegularChart';
import type {
  ChartType,
  ExtendedOptions,
  ExtendedSeriesOptionsType
} from '@/components/Chart/types/highcharts';
import { chartTableCells } from '@/features/Factsheet/components/FactsheetView/FactsheetWindow/FactsheetBody/Tabs/ChartTab/chartTableCells';
import useUserDetails from '@/hooks/useUserDetails';
import { generateMockSeries } from '@/mocks/chartSeries.mock';
import type { TemplateChartSeries } from '@/pages/Markets/MarketsComponents/types';

import LegendItem from './LegendItem';

const AuthenticatedChart = ({
  chartSeries,
  error,
  loading,
  chartOptions,
  chartType
}: {
  chartSeries: ExtendedSeriesOptionsType[] | undefined;
  error: boolean;
  loading: boolean;
  chartOptions: ExtendedOptions | undefined;
  chartType: ChartType | undefined;
}) => {
  const { isUserPremium, isUserExpired } = useUserDetails();
  return (
    <>
      <RegularChart
        chartSeries={isUserPremium && !isUserExpired ? chartSeries : [generateMockSeries()]}
        chartType={chartType}
        error={error}
        loading={loading}
        options={chartOptions}
        showLegend={false}
      />
      <Box sx={{ pt: 1, pb: 2 }}>
        <TableContainer
          component={Paper}
          sx={{ overflow: 'hidden', width: '100%' }}
        >
          <Table
            aria-label='api keys table'
            size='small'
            sx={{
              thead: {
                top: 0,
                zIndex: 1303
              }
            }}
          >
            <TableHead>
              <TableRow sx={{ '.MuiTableCell-head': { backgroundColor: 'background.paper' } }}>
                {chartTableCells.map((cell, index) => (
                  <TableCell
                    key={cell.name}
                    align={cell.align}
                    sx={{ pr: chartTableCells.length - 1 === index ? 0 : 5 }}
                  >
                    <Typography
                      sx={theme => ({ color: theme.palette.info.dark })}
                      variant='body2'
                    >
                      {cell.name}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody sx={{ maxHeight: 150 }}>
              {chartOptions?.series?.map(series => (
                <LegendItem
                  key={series.id}
                  series={series}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </>
  );
};

export const AuthenticatedFwdCurveChart = ({
  templateChartSeries
}: {
  templateChartSeries: TemplateChartSeries[];
}) => {
  const { loading, error, chartSeries } = useForwardCurveChartSeries(templateChartSeries);
  const { createOptions } = useForwardCurveOptions(14);
  const { options: chartOptions } = useChartOptions(chartSeries, createOptions);
  return (
    <AuthenticatedChart
      chartOptions={chartOptions}
      chartSeries={chartSeries}
      chartType='forwardSimple'
      error={error}
      loading={loading}
    />
  );
};

export const AuthenticatedSpotChart = ({
  templateChartSeries
}: {
  templateChartSeries: TemplateChartSeries[];
}) => {
  const { loading, error, chartSeries } = useChartSeries(templateChartSeries);
  const { createOptions } = useSpotOptions(14);
  const { options: chartOptions } = useChartOptions(
    chartSeries,

    createOptions
  );
  return (
    <AuthenticatedChart
      chartOptions={chartOptions}
      chartSeries={chartSeries}
      chartType='spot'
      error={error}
      loading={loading}
    />
  );
};
export default AuthenticatedChart;
